import React, { useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { getPhishingReport } from '../../data/reportHelpers';
import { getStatusColor } from '../../data/helpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiTarget, FiMousePointer, FiShield, FiAlertTriangle } = FiIcons;

const COLUMNS = [
  { key: 'name', label: 'Simulation' },
  { key: 'sendDate', label: 'Send Date' },
  { key: 'status', label: 'Status' },
  { key: 'sent', label: 'Sent' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'submitted', label: 'Submitted Creds' },
  { key: 'reported', label: 'Reported' },
  { key: 'clickRate', label: 'Click Rate %' },
  { key: 'reportRate', label: 'Report Rate %' },
];

const PhishingSimReport = ({ filters, raw }) => {
  const data = useMemo(() => getPhishingReport(raw, filters), [raw, filters]);

  const avgClick = (data.reduce((a, b) => a + b.clickRate, 0) / (data.length || 1)).toFixed(1);
  const avgReport = (data.reduce((a, b) => a + b.reportRate, 0) / (data.length || 1)).toFixed(1);
  const totalClicked = data.reduce((a, b) => a + b.clicked, 0);
  const totalReported = data.reduce((a, b) => a + b.reported, 0);

  const stats = [
    { label: 'Simulations', value: data.length, icon: FiTarget, iconColor: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Avg. Click Rate', value: `${avgClick}%`, icon: FiMousePointer, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
    { label: 'Avg. Report Rate', value: `${avgReport}%`, icon: FiShield, iconColor: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
    { label: 'Total Clicks', value: totalClicked, icon: FiAlertTriangle, iconColor: 'bg-orange-50 text-orange-600', border: 'border-orange-100' },
    { label: 'Total Reported', value: totalReported, icon: FiShield, iconColor: 'bg-teal-50 text-teal-600', border: 'border-teal-100' },
    { label: 'Best Report Rate', value: `${data.length ? Math.max(...data.map(d => d.reportRate)).toFixed(1) : 0}%`, icon: FiShield, iconColor: 'bg-violet-50 text-violet-600', border: 'border-violet-100' },
  ];

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Click Rate %', 'Report Rate %', 'Submission Rate %'], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: '3%', right: '4%', bottom: '18%', top: '5%', containLabel: true },
    xAxis: { type: 'category', data: data.map(d => d.name.substring(0, 18) + (d.name.length > 18 ? '…' : '')), axisLabel: { fontSize: 10, rotate: 8 } },
    yAxis: { type: 'value', axisLabel: { formatter: '{value}%' }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [
      { name: 'Click Rate %', type: 'bar', data: data.map(d => d.clickRate), itemStyle: { color: '#ef4444', borderRadius: [4, 4, 0, 0] } },
      { name: 'Report Rate %', type: 'bar', data: data.map(d => d.reportRate), itemStyle: { color: '#10b981', borderRadius: [4, 4, 0, 0] } },
      { name: 'Submission Rate %', type: 'bar', data: data.map(d => d.credentialSubmissionRate), itemStyle: { color: '#f97316', borderRadius: [4, 4, 0, 0] } },
    ],
  };

  return (
    <div className="space-y-4">
      <SummaryStatBar stats={stats} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Click Rate vs. Report Rate by Simulation</h3>
        <ReactECharts option={chartOption} style={{ height: 260 }} />
      </div>
      <ExportBar reportTitle="Phishing Simulation Report" data={data} columns={COLUMNS} summaryStats={stats.map(s => ({ label: s.label, value: s.value }))} rowCount={data.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Simulation</th>
                <th className="p-4">Date</th>
                <th className="p-4">Status</th>
                <th className="p-4">Sent</th>
                <th className="p-4">Opened</th>
                <th className="p-4">Clicked</th>
                <th className="p-4">Credentials</th>
                <th className="p-4">Reported</th>
                <th className="p-4">Click Rate</th>
                <th className="p-4">Report Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {data.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 font-medium text-slate-800 max-w-44">{row.name}</td>
                  <td className="p-4 text-xs text-slate-500">{row.sendDate}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(row.status)}`}>{row.status}</span>
                  </td>
                  <td className="p-4 text-slate-700">{row.sent}</td>
                  <td className="p-4 text-slate-700">{row.opened}</td>
                  <td className="p-4 text-red-600 font-semibold">{row.clicked}</td>
                  <td className="p-4 text-orange-600 font-semibold">{row.submitted}</td>
                  <td className="p-4 text-emerald-600 font-semibold">{row.reported}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 bg-slate-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-red-400" style={{ width: `${Math.min(row.clickRate, 100)}%` }} />
                      </div>
                      <span className={`text-xs font-bold ${row.clickRate > 15 ? 'text-red-600' : row.clickRate > 8 ? 'text-orange-600' : 'text-emerald-600'}`}>{row.clickRate}%</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs font-bold ${row.reportRate > 30 ? 'text-emerald-600' : row.reportRate > 15 ? 'text-blue-600' : 'text-slate-500'}`}>{row.reportRate}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PhishingSimReport;